import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("authvault_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    const ref = localStorage.getItem("authvault_ref") || null;
    const res = await api.post("/auth/register", { ...payload, ref });
    localStorage.setItem("authvault_token", res.data.token);
    localStorage.removeItem("authvault_ref");
    setUser(res.data.user);
    return res.data.user;
  };

  const handleGoogleSession = async (sessionId) => {
    const ref = localStorage.getItem("authvault_ref") || null;
    const res = await api.post("/auth/session", { session_id: sessionId, ref });
    localStorage.removeItem("authvault_ref");
    setUser(res.data.user);
    return res.data.user;
  };

  const refreshUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch (e) {
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) { /* ignore */ }
    localStorage.removeItem("authvault_token");
    setUser(null);
  };

  const updateProfile = async (payload) => {
    const res = await api.patch("/auth/profile", payload);
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, handleGoogleSession, logout, updateProfile, refreshUser, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};
